import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2, CheckCircle, UploadCloud, FileText, Landmark } from 'lucide-react';
import type { PaymentDocumentData } from '../../../hooks/expertDashboardHooks/usePaymentDocuments'; // Adjust path if needed

interface PaymentDocumentFormProps {
  initialData: PaymentDocumentData | null;
  uploadFile: (file: File) => Promise<string | null>;
  submitDocuments: (data: Partial<PaymentDocumentData>) => Promise<boolean>;
  loading: boolean;
  uploading: boolean;
}

export default function PaymentDocumentForm({ 
  initialData, 
  uploadFile, 
  submitDocuments, 
  loading, 
  uploading 
}: PaymentDocumentFormProps) {
  
  const { 
    register, 
    handleSubmit, 
    setValue, 
    watch, 
    reset, 
    formState: { errors, isValid } 
  } = useForm<Partial<PaymentDocumentData>>({
    mode: 'onChange' 
  });
  
  const [activeUpload, setActiveUpload] = useState<string | null>(null);

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
  }, [initialData, reset]);

  const panUrl = watch('pan_card_url');
  const aadhaarFrontUrl = watch('aadhaar_front_url');
  const aadhaarBackUrl = watch('aadhaar_back_url');
  const bankProofUrl = watch('bank_proof_url');

  const allFilesUploaded = !!(panUrl && aadhaarFrontUrl && aadhaarBackUrl && bankProofUrl);
  const isFormReady = isValid && allFilesUploaded;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof PaymentDocumentData) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setActiveUpload(fieldName);
    const url = await uploadFile(file);
    setActiveUpload(null);

    if (url) {
      setValue(fieldName, url, { shouldValidate: true });
    }
  };

  const onSubmit = async (data: Partial<PaymentDocumentData>) => {
    const success = await submitDocuments(data);
    if (success) {
      alert("Documents submitted successfully! Waiting for admin approval.");
    }
  };

  const renderFileInput = (label: string, fieldName: keyof PaymentDocumentData, currentUrl: string | null | undefined) => {
    const isUploadingThis = activeUpload === fieldName;
    const hasFile = !!currentUrl;

    return (
      <div className="flex flex-col space-y-2">
        <label className="text-sm font-semibold text-slate-700">{label}</label>
        <div className="relative">
          <input
            type="file"
            accept="image/jpeg, image/png, application/pdf"
            onChange={(e) => handleFileUpload(e, fieldName)}
            disabled={uploading}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
          />
          <div className={`flex items-center justify-center px-4 py-3 border-2 border-dashed rounded-lg transition-colors ${hasFile ? 'border-green-400 bg-green-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'}`}>
            {isUploadingThis ? (
              <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            ) : hasFile ? (
              <span className="flex items-center text-sm text-green-700 font-medium">
                <CheckCircle className="w-4 h-4 mr-2" /> Uploaded Successfully
              </span>
            ) : (
              <span className="flex items-center text-sm text-slate-500 font-medium">
                <UploadCloud className="w-4 h-4 mr-2" /> Click to upload file
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6 md:p-8 space-y-8">
      
      {/* Section 1: PAN */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center text-slate-800 border-b pb-2">
          <FileText className="w-5 h-5 mr-2 text-slate-400" /> PAN Card Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">PAN Number</label>
            <input
              {...register('pan_number', { required: "PAN number is required" })}
              type="text"
              placeholder="ABCDE1234F"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
            />
            {errors.pan_number && <p className="text-red-500 text-xs">{errors.pan_number.message}</p>}
          </div>
          {renderFileInput("Upload PAN Card Image", "pan_card_url", panUrl)}
        </div>
      </div>

      {/* Section 2: Aadhaar */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center text-slate-800 border-b pb-2">
          <FileText className="w-5 h-5 mr-2 text-slate-400" /> Aadhaar Card Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Aadhaar Number</label>
            <input
              {...register('aadhaar_number', { required: "Aadhaar number is required" })}
              type="text"
              placeholder="1234 5678 9012"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {errors.aadhaar_number && <p className="text-red-500 text-xs">{errors.aadhaar_number.message}</p>}
          </div>
          {renderFileInput("Aadhaar Front Image", "aadhaar_front_url", aadhaarFrontUrl)}
          <div className="md:col-start-2">
            {renderFileInput("Aadhaar Back Image", "aadhaar_back_url", aadhaarBackUrl)}
          </div>
        </div>
      </div>

      {/* Section 3: Bank Details */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold flex items-center text-slate-800 border-b pb-2">
          <Landmark className="w-5 h-5 mr-2 text-slate-400" /> Bank Account Details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Account Holder Name</label>
            <input
              {...register('bank_account_name', { required: "Account name is required" })}
              type="text"
              placeholder="As per bank records"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {errors.bank_account_name && <p className="text-red-500 text-xs">{errors.bank_account_name.message}</p>}
          </div>

          {/* 🟢 NEW: Account Type Dropdown */}
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Account Type</label>
            <select
              {...register('bank_account_type', { required: "Account type is required" })}
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Select Account Type</option>
              <option value="Savings Account">Savings Account</option>
              <option value="Current Account">Current Account</option>
            </select>
            {errors.bank_account_type && <p className="text-red-500 text-xs">{errors.bank_account_type.message}</p>}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Account Number</label>
            <input
              {...register('bank_account_number', { required: "Account number is required" })}
              type="text"
              placeholder="Account Number"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {errors.bank_account_number && <p className="text-red-500 text-xs">{errors.bank_account_number.message}</p>}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">IFSC Code</label>
            <input
              {...register('bank_ifsc_code', { required: "IFSC code is required" })}
              type="text"
              placeholder="SBIN0001234"
              className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 uppercase"
            />
            {errors.bank_ifsc_code && <p className="text-red-500 text-xs">{errors.bank_ifsc_code.message}</p>}
          </div>

          <div className="md:col-span-2">
             {renderFileInput("Cancelled Cheque / Passbook", "bank_proof_url", bankProofUrl)}
          </div>
          
        </div>
      </div>

      <div className="pt-4 border-t border-slate-100 flex justify-end">
        <button
          type="submit"
          disabled={loading || uploading || !isFormReady}
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
          {initialData?.verification_status === 'pending' ? 'Update Documents' : 'Submit for Verification'}
        </button>
      </div>
    </form>
  );
}